import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import ProjectSelect from '@/components/ProjectSelect';
import DocAccountsBlock from '@/shared/components/DocAccountsBlock';
import { refs } from '@/api/refs';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import {
  sickLeave,
  sickLeaveCalculation,
  type SickLeaveType,
  type SickLeaveCalculation,
} from '@/api/sick_leave';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { useConfirm } from '@/components/ConfirmProvider';
import InfoQ from '@/shared/components/InfoQ';

const now = new Date();
const fmt2 = (v: string | number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABELS: Record<SickLeaveType, string> = {
  GENERAL_ILLNESS: 'Загальне захворювання',
  MATERNITY: 'Вагітність і пологи',
  CHILD_CARE: 'Догляд за дитиною',
  WORK_ACCIDENT: 'Виробничий нещасний випадок',
};
// WORK_ACCIDENT (нещасний випадок) тимчасово прибрано з UI (TECHDEBT — потребує
// окремої кореспонденції 656/ФССНВ + перевірки). Розрахунок backend підтримує.
// CHILD_CARE (догляд за дитиною) прибрано з МЕНЮ (рішення власника 2026-06-29) —
// як WORK_ACCIDENT. Розрахунок backend підтримує; TYPE_LABELS лишається для показу
// існуючих/імпортованих документів цього виду.
const TYPE_ORDER: SickLeaveType[] = ['GENERAL_ILLNESS', 'MATERNITY'];

const FUNDING_HINT: Record<SickLeaveType, string> = {
  GENERAL_ILLNESS: 'Перші 5 днів — за рахунок роботодавця, решта — за рахунок ПФУ. % за стажем.',
  MATERNITY: 'Усі дні — за рахунок ПФУ, завжди 100%, ПДФО та ВЗ НЕ утримуються.',
  CHILD_CARE: 'Усі дні — за рахунок ПФУ. % за стажем.',
  WORK_ACCIDENT: 'Усі дні — за рахунок Фонду нещасних випадків, 100%.',
};

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  sick_leave_type: SickLeaveType;
  start_date: string;
  end_date: string;
  certificate_number: string;
  certificate_date: string;
  diagnosis_code: string;
  insurance_stage_years: string;
  comment: string;
}

const todayISO = now.toISOString().slice(0, 10);
const EMPTY: FormState = {
  organization_id: '', document_number: '', document_date: now.toISOString().slice(0, 16),
  employee_id: '', sick_leave_type: 'GENERAL_ILLNESS',
  start_date: todayISO, end_date: todayISO,
  certificate_number: '', certificate_date: todayISO,
  diagnosis_code: '', insurance_stage_years: '0', comment: '',
};

function daysBetween(a: string, b: string): number {
  if (!a || !b) return 0;
  const d = (new Date(b).getTime() - new Date(a).getTime()) / 86400000;
  return d >= 0 ? Math.floor(d) + 1 : 0;
}

export default function SickLeaveEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('documents.sick_leave.create');

  const docQuery = useQuery({
    queryKey: ['sick-leave', id],
    queryFn: () => sickLeave.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [preview, setPreview] = useState<SickLeaveCalculation | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  // id збереженого DRAFT-розрахунку (як у наказі відпустки) — щоб «Розрахувати»
  // оновлював чернетку (PATCH), а не дублював.
  const [existingCalcId, setExistingCalcId] = useState<string | null>(null);
  const [posted, setPosted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stageEdited, setStageEdited] = useState(false);
  // Ручний override середньоденної (бухгалтер може перебити пораховану).
  const [avgOverride, setAvgOverride] = useState('');
  // Проект / напрям (упр. облік) — потрапляє у субконто-3 проводок при проведенні.
  const [projectId, setProjectId] = useState('');
  const [docTab, setDocTab] = useState<'calc' | 'accounts'>('calc');
  const [expCode, setExpCode] = useState('949');
  // Незбережені правки → деактивують «Розрахувати» (preview застарів).
  const [dirty, setDirty] = useState(false);
  const confirm = useConfirm();

  const empQ = useQuery({
    queryKey: ['employees', form.organization_id],
    queryFn: () => refs.employees(form.organization_id, { active_only: true }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      // Авто-вибір org: availableOrgs (право create) з fallback на повний список
      // (OWNER/повільний /auth/me) — інакше organization_id порожній → save падає.
      const firstOrg = availableOrgs[0]?.id ?? orgList.data?.items[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id, document_number: d.document_number,
        document_date: d.document_date.slice(0, 16), employee_id: d.employee_id,
        sick_leave_type: d.sick_leave_type, start_date: d.start_date, end_date: d.end_date,
        certificate_number: d.certificate_number, certificate_date: d.certificate_date,
        diagnosis_code: d.diagnosis_code ?? '',
        insurance_stage_years: d.insurance_stage_years, comment: d.comment ?? '',
      });
      setSavedId(d.id);
      setPosted(d.posted);
      setStageEdited(true);
      setDirty(false);
      if (d.sick_leave_calculation_id) {
        setExistingCalcId(d.sick_leave_calculation_id);
        sickLeaveCalculation.get(d.sick_leave_calculation_id).then((c) => {
          setPreview(c);
          setProjectId(c.project_id ?? '');
          setExpCode(c.expense_account_code ?? '949');
        }).catch(() => {});
      }
    }
  }, [isNew, docQuery.data, availableOrgs, orgList.data]);

  // Авто-стаж із hire_date обраного співробітника (підказка, редаговане).
  const selectedEmp = empQ.data?.find((e) => e.id === form.employee_id);
  useEffect(() => {
    if (stageEdited || !selectedEmp?.hire_date || !form.start_date) return;
    const yrs = (new Date(form.start_date).getTime() - new Date(selectedEmp.hire_date).getTime())
      / (365.25 * 86400000);
    if (yrs >= 0) {
      setForm((f) => ({ ...f, insurance_stage_years: yrs.toFixed(1) }));
    }
  }, [selectedEmp, form.start_date, stageEdited]);

  const days = daysBetween(form.start_date, form.end_date);
  const isPosted = posted || (preview?.posted ?? false);
  const isMaternity = form.sick_leave_type === 'MATERNITY';

  function buildCreate() {
    return {
      organization_id: form.organization_id,
      document_number: form.document_number || undefined,
      document_date: form.document_date,
      employee_id: form.employee_id,
      sick_leave_type: form.sick_leave_type,
      start_date: form.start_date, end_date: form.end_date,
      certificate_number: form.certificate_number,
      certificate_date: form.certificate_date,
      diagnosis_code: form.diagnosis_code || null,
      insurance_stage_years: form.insurance_stage_years || '0',
      comment: form.comment || null,
    };
  }

  // Зберегти SickLeave (якщо ще нема) → повернути id.
  async function ensureSaved(): Promise<string> {
    if (savedId) return savedId;
    if (!form.organization_id) throw new Error('Оберіть організацію');
    if (!form.employee_id) throw new Error('Оберіть працівника');
    const doc = await sickLeave.create(buildCreate());
    setSavedId(doc.id);
    qc.invalidateQueries({ queryKey: ['sick-leaves'] });
    return doc.id;
  }

  // Зберегти лист (новий) → відкрити сторінку збереженого (як наказ відпустки).
  const create = useMutation({
    mutationFn: () => sickLeave.create(buildCreate()),
    onSuccess: (doc) => {
      setSavedId(doc.id); setDirty(false); setError(null);
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      navigate(`/hr/sick-leaves/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });

  // Розрахувати — ЗБЕРIГАЄ DRAFT-розрахунок у БД (як у наказі відпустки), а не лише
  // preview у пам'яті. Якщо розрахунок цього листа ВЖЕ є — ОНОВИТИ (PATCH), інакше
  // створити чернетку (createDraft). Так при переоткритті листа таблиця лишається.
  const calc = useMutation({
    mutationFn: () => {
      if (existingCalcId) {
        return sickLeaveCalculation.update(existingCalcId, {
          average_daily_override: avgOverride ? avgOverride : null,
          project_id: projectId || null,
          expense_account_code: expCode || null,
        });
      }
      return sickLeaveCalculation.createDraft({
        organization_id: form.organization_id, sick_leave_id: savedId!,
        document_date: form.document_date,
        average_daily_override: avgOverride ? avgOverride : null,
        project_id: projectId || null,
        expense_account_code: expCode || undefined,
      });
    },
    onSuccess: (r) => {
      setPreview(r); setExistingCalcId(r.id); setError(null);
      if (!avgOverride) setAvgOverride(trimNum(r.average_daily_wage));
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['sick-leave', savedId] });
      qc.invalidateQueries({ queryKey: ['sick-leave-calculations'] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розрахунку'),
  });

  const post = useMutation({
    mutationFn: async () => {
      const sid = await ensureSaved();
      return sickLeaveCalculation.calculateAndPost({
        organization_id: form.organization_id, sick_leave_id: sid,
        document_date: form.document_date,
        average_daily_override: avgOverride ? avgOverride : null,
        project_id: projectId || null,
        expense_account_code: expCode || undefined,
      });
    },
    onSuccess: (r) => {
      // calculate-and-post (idempotency) міг видалити старий DRAFT і створити новий
      // проведений → оновити existingCalcId на актуальний id.
      setPreview(r); setExistingCalcId(r.id); setPosted(true);
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['sick-leave-calculations'] });
      if (savedId) navigate(`/hr/sick-leaves/${savedId}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });

  // PATCH лікарняного (SickLeaveUpdate, без org/працівника). Включає тип+дати+дні —
  // інакше зміна виду/дат не зберігалась і розрахунок лишався по старому виду.
  function buildUpdate() {
    return {
      document_date: form.document_date,
      sick_leave_type: form.sick_leave_type,
      start_date: form.start_date,
      end_date: form.end_date,
      days_count: days,
      certificate_number: form.certificate_number,
      certificate_date: form.certificate_date,
      diagnosis_code: form.diagnosis_code || null,
      insurance_stage_years: form.insurance_stage_years || '0',
      comment: form.comment || null,
    };
  }
  // Зберегти зміни вже збереженого лікарняного (PATCH) → дозволяє «Розрахувати» знову.
  const updateSick = useMutation({
    mutationFn: () => sickLeave.update(savedId!, buildUpdate() as never),
    onSuccess: () => {
      setError(null); setPreview(null); setDirty(false);
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['sick-leave', savedId] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });
  // Розпровести: «Провести» проводить РОЗРАХУНОК (sick_leave_calculation), сам лист
  // лишається DRAFT. Тож розпроводимо ЛИШЕ розрахунок (не лист — інакше 409 «Не проведено»).
  const unpostSick = useMutation({
    mutationFn: () => {
      if (!preview?.id) throw new Error('Немає проведеного розрахунку');
      return sickLeaveCalculation.unpost(preview.id);
    },
    onSuccess: (r) => {
      setPosted(false); setPreview(r); setError(null);
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['sick-leave-calculations'] });
      qc.invalidateQueries({ queryKey: ['sick-leave', savedId] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розпроведення'),
  });
  // Видалити лікарняний.
  const removeSick = useMutation({
    mutationFn: () => sickLeave.remove(savedId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/payrolls');
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка видалення'),
  });

  // Зберегти лист можна коли заповнені обов'язкові поля.
  const canSave = !!form.organization_id && !!form.employee_id && days > 0
    && !!form.certificate_number && !isPosted;
  // Розрахувати — лише для ЗБЕРЕЖЕНОГО листа без незбережених правок (як наказ відпустки).
  const canCalc = !!savedId && !dirty && !isPosted;

  // Зміна ключових полів → скинути розрахунок + dirty (треба зберегти і перерахувати).
  function setF(patch: Partial<FormState>) {
    setForm((f) => ({ ...f, ...patch }));
    if (!isPosted) { setPreview(null); setDirty(true); }
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew && !savedId ? 'Нове нарахування (лікарняний)' : `Нарахування (лікарняний) № ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>✓ проведено</span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/payrolls')}
          onSave={() => isNew ? create.mutate() : updateSick.mutate()}
          saveDisabled={!canSave || create.isPending || updateSick.isPending}
          isPosted={isPosted}
          isNew={isNew && !savedId}
          postable={!!savedId}
          onPost={() => post.mutate()}
          postPending={post.isPending}
          postTitle="Розраховує та проводить лікарняний"
          onUnpost={() => unpostSick.mutate()}
          unpostPending={unpostSick.isPending}
          onDelete={() => { confirm('Видалити лікарняний?').then((ok) => { if (ok) removeSick.mutate(); }); }}
          deletePending={removeSick.isPending}
        />
      </div>

      {isPosted && (
        <div style={{ background: '#dcfce7', color: '#166534', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>✓ Лікарняний проведено.</div>
      )}
      {error && (
        <div style={{ background: '#fee', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      {/* Шапка: організація / працівник / дата / причина — компактно в один ряд. */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ width: 200, margin: 0 }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew || !!savedId}
            onChange={(e) => setF({ organization_id: e.target.value, employee_id: '' })}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ width: 220, margin: 0 }}>
          <label>Працівник *</label>
          <select value={form.employee_id} disabled={isPosted}
            onChange={(e) => { setStageEdited(false); setF({ employee_id: e.target.value }); }}>
            <option value="">—</option>
            {empQ.data?.map((e) => <option key={e.id} value={e.id}>{e.code ? `${e.code} — ` : ''}{e.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ width: 130, margin: 0 }}>
          <label>Дата документа</label>
          <DatePopover value={form.document_date.slice(0, 10)} disabled={isPosted}
            onChange={(v) => setF({ document_date: `${v}T00:00` })} />
        </div>
        <div className="field" style={{ width: 240, margin: 0 }}>
          <label>Причина (тип) *</label>
          <select value={form.sick_leave_type} disabled={isPosted}
            onChange={(e) => setF({ sick_leave_type: e.target.value as SickLeaveType })}>
            {TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        </div>
      </div>
      <small style={{ color: 'var(--muted)', display: 'block', marginTop: '0.2rem' }}>
        {FUNDING_HINT[form.sick_leave_type]}
      </small>

      {/* Період + дні + реквізити листа + стаж — компактно в один ряд. */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.6rem' }}>
        <div className="field" style={{ width: 130, margin: 0 }}>
          <label>Дата початку *</label>
          <DatePopover value={form.start_date} disabled={isPosted} onChange={(v) => setF({ start_date: v })} />
        </div>
        <div className="field" style={{ width: 130, margin: 0 }}>
          <label>Дата закінчення *</label>
          <DatePopover value={form.end_date} disabled={isPosted} onChange={(v) => setF({ end_date: v })} />
        </div>
        <div className="field" style={{ width: 70, margin: 0 }}>
          <label>Днів</label>
          <input value={days} readOnly disabled style={{ textAlign: 'center', background: '#f1f5f9' }} />
        </div>
        <div className="field" style={{ width: 140, margin: 0 }}>
          <label>№ листа *</label>
          <input value={form.certificate_number} disabled={isPosted}
            onChange={(e) => setF({ certificate_number: e.target.value })} />
        </div>
        <div className="field" style={{ width: 130, margin: 0 }}>
          <label>Дата листа</label>
          <DatePopover value={form.certificate_date} disabled={isPosted} onChange={(v) => setF({ certificate_date: v })} />
        </div>
        <div className="field" style={{ width: 100, margin: 0 }}>
          <label>Діагноз</label>
          <input value={form.diagnosis_code} disabled={isPosted} placeholder="код"
            onChange={(e) => setF({ diagnosis_code: e.target.value })} />
        </div>
      </div>

      {/* Стаж + середньоденна — окремий ряд. */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.6rem' }}>
        <div className="field" style={{ width: 130, margin: 0 }}>
          <label>Стаж (років)</label>
          <input type="number" step="0.1" min={0} value={form.insurance_stage_years}
            disabled={isPosted || isMaternity}
            title={isMaternity ? 'Для декретних завжди 100% — стаж не впливає' : undefined}
            onChange={(e) => { setStageEdited(true); setF({ insurance_stage_years: e.target.value }); }} />
        </div>
        <div className="field" style={{ width: 130, margin: 0 }}>
          <label>Середньоденна</label>
          <input type="number" step="0.01" min={0} value={avgOverride} disabled={isPosted}
            placeholder="авто з заробітку"
            onChange={(e) => { setAvgOverride(e.target.value); if (!isPosted) setPreview(null); }} />
        </div>
        <div className="field" style={{ minWidth: 220, margin: 0 }}>
          <label>Проект / напрям</label>
          <ProjectSelect
            value={projectId}
            organizationId={form.organization_id}
            onChange={(v) => setProjectId(v ?? '')}
            disabled={isPosted} />
        </div>
      </div>
      {!isMaternity && selectedEmp?.hire_date && (
        <small style={{ color: '#b45309', display: 'block', marginTop: '0.25rem' }}>
          ⚠ Стаж підставлено з дати прийому ({selectedEmp.hire_date}) — БЕЗ урахування
          попередніх місць роботи. Перевірте за трудовою книжкою / реєстром ЦСПС.
        </small>
      )}

      {/* Кнопка Розрахувати — активна лише після збереження листа (як наказ відпустки). */}
      {!isPosted && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
            <button className="primary" disabled={!canCalc || calc.isPending}
              onClick={() => calc.mutate()}
              title={!savedId
                ? 'Спершу збережіть лікарняний — тоді можна розрахувати'
                : dirty
                  ? 'Ви змінили дані. Спершу збережіть («Зберегти») — тоді розрахунок оновиться'
                  : 'Перерахунок брутто/ПДФО/ВЗ/ЄСВ за дні × середню.'}>
              Розрахувати
            </button>
          </div>
          {!savedId && (
            <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0.3rem 0 0', textAlign: 'right' }}>
              Кнопка «Розрахувати» стане активною після збереження лікарняного.
            </p>
          )}
          {savedId && dirty && (
            <p style={{ color: '#b45309', fontSize: '0.8rem', margin: '0.3rem 0 0', textAlign: 'right' }}>
              Ви змінили дані. Натисніть «Зберегти» — після цього можна перерахувати.
            </p>
          )}
        </>
      )}

      {/* Результат розрахунку */}
      {/* Закладки: розрахунок ↔ рахунки обліку (рішення власника 2026-07-07). */}
      {preview && (
        <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6,
          overflow: 'hidden', width: 'fit-content', margin: '0.5rem 0' }}>
          {([['calc', 'Розрахунок'], ['accounts', 'Рахунки обліку']] as const).map(([t, l]) => (
            <button key={t} onClick={() => setDocTab(t)}
              style={{ padding: '0.3rem 0.9rem', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                background: docTab === t ? 'var(--primary)' : 'transparent',
                color: docTab === t ? '#fff' : 'var(--text-secondary)' }}>
              {l}
            </button>
          ))}
        </div>
      )}
      {preview && docTab === 'accounts' && form.employee_id && (
        <DocAccountsBlock
          organizationId={form.organization_id}
          employeeIds={[form.employee_id]}
          paymentKind="SICK"
          headerCodes={{ expense: expCode, payroll: '663', pdfo: '6411', wz: '6415', esv: '651' }}
          expenseCode={expCode}
          onExpenseChange={(v) => setExpCode(v)}
          disabled={isPosted}
          nameOf={() => selectedEmp?.name ?? '—'}
        />
      )}
      {docTab === 'calc' && preview && (
        <div style={{ marginTop: '1rem', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem' }}>
          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem' }}>Розрахунок</h4>
          {/* Інфо над таблицею. */}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.82rem',
            color: '#475569', marginBottom: '0.5rem' }}>
            <span><strong>Працівник:</strong> {selectedEmp?.name ?? '—'}</span>
            <span><strong>Період:</strong> {preview.calc_period_start} — {preview.calc_period_end}</span>
            <span><strong>Середня:</strong> {fmt2(preview.average_daily_wage)}{preview.average_from_import ? ' ⚠' : ''}</span>
            <span><strong>% за стажем:</strong> {preview.applied_percentage_rate}%</span>
          </div>
          {/* Широка таблиця: 2 рядки (Роботодавець / Фонд) + Разом. */}
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'right' }}>
                <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem' }}>Джерело</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>Днів</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>Брутто</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>ПДФО</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>ВЗ</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>ЄСВ</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>До виплати</th>
              </tr>
            </thead>
            <tbody>
              {/* Роботодавець — лише якщо є його дні (перші 5 при загальному захворюванні).
                  Догляд за дитиною / пологи — все за рахунок Фонду, рядка роботодавця немає. */}
              {Number(preview.employer_days_count) > 0 && (
                <tr style={{ textAlign: 'right', borderTop: '1px solid #e2e8f0' }}>
                  <td style={{ textAlign: 'left', padding: '0.3rem 0.4rem' }}>Роботодавець</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{preview.employer_days_count}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.employer_amount)}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.employer_pdfo_amount ?? 0)}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.employer_wz_amount ?? 0)}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.employer_esv_amount ?? 0)}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.employer_net_amount ?? 0)}</td>
                </tr>
              )}
              {Number(preview.pfu_days_count) > 0 && (
                <tr style={{ textAlign: 'right', borderTop: '1px solid #eee' }}>
                  <td style={{ textAlign: 'left', padding: '0.3rem 0.4rem' }}>
                    {preview.funding_source_marker === 'WORK_ACCIDENT_FUND' ? 'Фонд НВ' : 'Фонд (ПФУ)'}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{preview.pfu_days_count}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.pfu_amount)}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.pfu_pdfo_amount ?? 0)}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.pfu_wz_amount ?? 0)}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.pfu_esv_amount ?? 0)}</td>
                  <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.pfu_net_amount ?? 0)}</td>
                </tr>
              )}
              <tr style={{ textAlign: 'right', borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                <td style={{ textAlign: 'left', padding: '0.3rem 0.4rem' }}>Разом</td>
                <td style={{ padding: '0.3rem 0.4rem' }}>{preview.days_count}</td>
                <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.total_gross_amount)}</td>
                <td style={{ padding: '0.3rem 0.4rem' }}>
                  {fmt2(preview.pdfo_amount)} <InfoQ title={isMaternity
                    ? 'Декретні не оподатковуються ПДФО (Закон 1105)'
                    : `Податок на доходи фізосіб — ${trimNum(preview.pdfo_rate)}% від брутто.`} />
                </td>
                <td style={{ padding: '0.3rem 0.4rem' }}>
                  {fmt2(preview.wz_amount)} <InfoQ title={isMaternity
                    ? 'Декретні не оподатковуються військовим збором (Закон 1105)'
                    : `Військовий збір — ${trimNum(preview.wz_rate)}% від брутто.`} />
                </td>
                <td style={{ padding: '0.3rem 0.4rem' }}>
                  {fmt2(preview.esv_amount)} <InfoQ title={esvTipDoc(preview.esv_amount, preview.esv_rate)} />
                </td>
                <td style={{ padding: '0.3rem 0.4rem' }}>{fmt2(preview.net_amount)}</td>
              </tr>
            </tbody>
          </table>
          </div>

          {/* Платіжка ЄСВ з частини Фонду — для пакета документів у Фонд. */}
          {Number(preview.pfu_esv_amount ?? 0) > 0 && (
            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
              background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '0.82rem' }}>
              💸 ЄСВ до сплати з частини Фонду: <strong>{fmt2(preview.pfu_esv_amount ?? 0)}</strong> грн
              (окрема платіжка для пакета документів у Фонд).
            </div>
          )}

          {preview.average_from_import && (
            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
              background: '#fef3c7', border: '1px solid #f59e0b', fontSize: '0.8rem',
              color: '#b45309' }}>
              ⚠ Середня врахувала імпортовані з 1С нарахування — перевірте суму.
            </div>
          )}

          {(preview.monthly_parts?.length ?? 0) > 1 && (
            <div style={{ marginTop: '0.75rem' }}>
              <h4 style={{ margin: '0 0 0.3rem', fontSize: '0.85rem', color: '#b45309' }}>
                По місяцях (перехідний лікарняний — для звітності)
              </h4>
              <table style={{ fontSize: '0.8rem', borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'right' }}>
                    <th style={{ textAlign: 'left', padding: '0.2rem 0.4rem' }}>Місяць</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>Днів</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>Роботодавець</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>ПФУ/Фонд</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>Брутто</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>ПДФО</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>ВЗ</th>
                    <th style={{ padding: '0.2rem 0.4rem' }}>ЄСВ</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.monthly_parts!.map((m) => (
                    <tr key={`${m.period_year}-${m.period_month}`} style={{ textAlign: 'right', borderTop: '1px solid #eee' }}>
                      <td style={{ textAlign: 'left', padding: '0.15rem 0.4rem' }}>
                        {m.period_year}-{String(m.period_month).padStart(2, '0')}
                      </td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{m.days_in_month}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{m.employer_days_in_month} дн · {fmt2(m.employer_amount)}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{m.pfu_days_in_month} дн · {fmt2(m.pfu_amount)}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{fmt2(m.total_gross_amount)}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{fmt2(m.pdfo_amount)}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{fmt2(m.wz_amount)}</td>
                      <td style={{ padding: '0.15rem 0.4rem' }}>{fmt2(m.esv_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

